/* Quas stockist map: Google Maps JavaScript API provider module.
   Deferred progressive enhancement over the server-rendered metaobject list.
   Implements the frozen Work Package 05G contract:

   - Loads https://maps.googleapis.com/maps/api/js exactly once per page, only
     after the map shell is eligible (IntersectionObserver near-viewport, or an
     explicit Show on map interaction), with key, v=quarterly, loading=async
     and one generated global callback. No theme.liquid script, no head
     preload, no js-api-loader, no libraries/map_ids/auth_referrer_policy/
     channel/solution_channel parameters.
   - Imports only the "maps" and "marker" libraries via google.maps.importLibrary.
   - AdvancedMarkerElement pins only, backed by the production map ID from the
     section-scoped JSON configuration block. DEMO_MAP_ID and the legacy
     google.maps.Marker constructor are prohibited.
   - Map input is the serialised public venue JSON: physical records with a
     valid coordinate pair only. Events & Partners, list-only and invalid
     records never produce a pin.
   - One bounded 12-second load path. Script error, missing callback,
     importLibrary rejection, gm_authFailure, unavailable Advanced Markers
     capability, mount exception or timeout all invoke one idempotent failure
     handler that restores the static fallback and leaves the list usable.
     No retry loop runs within a visit.
   - No Places, Autocomplete, Geocoding, Routes or Geolocation API usage.

    WP11 test hooks: [data-quas-stockist-config] presence, the
   data-map-status attribute (placeholder -> loading -> ready | error), and
   the quas-stockist-map:ready / quas-stockist-map:error events with redacted
    reason codes only. Key and map-ID values are never logged. */

/* Shopify asset revision: stockist feedback runtime refresh. */

(() => {
  const GOOGLE_MAPS_SRC = 'https://maps.googleapis.com/maps/api/js';
  const API_CHANNEL = 'quarterly';
  const LOAD_TIMEOUT_MS = 12000;
  const OBSERVER_ROOT_MARGIN = '200px 0px';
  const UK_VIEWPORT = { center: { lat: 54.8, lng: -2.9 }, zoom: 5 };
  const SINGLE_MARKER_ZOOM = 13;
  const CARD_TARGET_ZOOM = 15;
  const ORIGIN_ZOOM = 11;
  const NEAREST_CLUSTER_SIZE = 5;
  const FIT_PADDING_PX = 48;

  const cssVar = (name, fallback) => {
    const value = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
  };

  const pinStyles = () => ({
    base: {
      background: cssVar('--quas-terracotta-dark', '#ba4f21'),
      borderColor: cssVar('--quas-terracotta', '#db7b51'),
      glyphColor: cssVar('--quas-cream', '#f9f5de'),
    },
    selected: {
      background: cssVar('--quas-walnut', '#43270f'),
      borderColor: cssVar('--quas-walnut-deep', '#2c1908'),
      glyphColor: cssVar('--quas-cream', '#f9f5de'),
      scale: 1.15,
    },
  });

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isMappable = (venue) =>
    Boolean(venue) &&
    typeof venue.name === 'string' &&
    venue.name.trim() !== '' &&
    (venue.category === 'shop' || venue.category === 'food_drink' || venue.category === 'wellness') &&
    typeof venue.latitude === 'number' &&
    Number.isFinite(venue.latitude) &&
    typeof venue.longitude === 'number' &&
    Number.isFinite(venue.longitude) &&
    venue.latitude >= -90 &&
    venue.latitude <= 90 &&
    venue.longitude >= -180 &&
    venue.longitude <= 180;

  const haversineMiles = (lat1, lng1, lat2, lng2) => {
    const rad = Math.PI / 180;
    const dLat = (lat2 - lat1) * rad;
    const dLng = (lng2 - lng1) * rad;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 3958.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // One script element is ever appended per page. The shared promise settles
  // once; a rejected or timed-out load is never retried within the visit.
  let googleScriptPromise = null;
  const authFailureHandlers = new Set();
  let authHook = null;

  const installAuthHook = () => {
    if (authHook) return;
    const previous = window.gm_authFailure;
    const handler = () => {
      authFailureHandlers.forEach((handler) => handler());
      if (typeof previous === 'function') previous();
    };
    authHook = { handler, previous };
    window.gm_authFailure = handler;
  };

  const removeAuthHook = (handler) => {
    authFailureHandlers.delete(handler);
    if (authFailureHandlers.size === 0 && authHook) {
      if (window.gm_authFailure === authHook.handler) {
        window.gm_authFailure = authHook.previous;
      }
      authHook = null;
    }
  };

  const loadGoogleScript = (key) => {
    if (googleScriptPromise) return googleScriptPromise;
    googleScriptPromise = new Promise((resolve, reject) => {
      const callbackName =
        '__quasGmapsOnLoad' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      window[callbackName] = () => {
        delete window[callbackName];
        resolve();
      };
      const script = document.createElement('script');
      const params = new URLSearchParams({
        key,
        v: API_CHANNEL,
        loading: 'async',
        callback: callbackName,
      });
      script.src = `${GOOGLE_MAPS_SRC}?${params.toString()}`;
      script.async = true;
      script.onerror = () => {
        delete window[callbackName];
        reject(new Error('script-error'));
      };
      document.head.append(script);
    });
    return googleScriptPromise;
  };

  const init = (root) => {
    const shell = root.querySelector('[data-quas-stockist-map]');
    const configEl = root.querySelector('[data-quas-stockist-config]');
    const dataEl = root.querySelector('script[type="application/json"][id^="quas-stockist-data-"]');
    if (!shell || !configEl || !dataEl) return; // Provider disabled: fallback stays intact.

    let config;
    let dataset;
    try {
      config = JSON.parse(configEl.textContent);
      dataset = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }
    if (
      !config ||
      typeof config.key !== 'string' ||
      config.key === '' ||
      typeof config.mapId !== 'string' ||
      config.mapId === '' ||
      !dataset ||
      !Array.isArray(dataset.venues)
    ) {
      return;
    }

    const staticEl = shell.querySelector('[data-map-static]');
    const loadingEl = shell.querySelector('[data-map-loading]');
    const errorEl = shell.querySelector('[data-map-error]');

    const state = { phase: 'idle', timer: null, observer: null };
    let map = null;
    let canvasEl = null;
    let userMarker = null;
    let selectedHandle = null;
    let pendingCardSelection = null;
    let latestDetail = { visibleHandles: null, origin: null, selectedHandle: null, eventsMode: false };
    const markers = new Map();
    let markerClasses = null;

    const setStatus = (status) => {
      shell.setAttribute('data-map-status', status);
    };

    const fail = (reason) => {
      if (state.phase !== 'loading' && state.phase !== 'ready') return;
      state.phase = 'failed';
      if (state.timer) window.clearTimeout(state.timer);
      state.timer = null;
      if (state.observer) state.observer.disconnect();
      state.observer = null;
      removeAuthHook(onAuthFailure);
      markers.forEach((entry) => {
        entry.marker.map = null;
      });
      markers.clear();
      if (userMarker) userMarker.map = null;
      userMarker = null;
      map = null;
      if (canvasEl) canvasEl.remove();
      canvasEl = null;
      pendingCardSelection = null;
      root.querySelectorAll('[data-show-on-map]').forEach((button) => {
        button.hidden = true;
      });
      if (loadingEl) loadingEl.hidden = true;
      if (staticEl) staticEl.hidden = false;
      if (errorEl) errorEl.hidden = false;
      setStatus('error');
      root.dispatchEvent(new CustomEvent('quas-stockist-map:error', { detail: { reason } }));
    };

    const onAuthFailure = () => fail('auth');

    const selectMarker = (handle) => {
      selectedHandle = handle || null;
      markers.forEach((entry, entryHandle) => {
        const active = selectedHandle !== null && entryHandle === selectedHandle;
        entry.marker.content = new markerClasses.marker.PinElement(
          active ? pinStyles().selected : pinStyles().base
        );
        entry.marker.zIndex = active ? 2 : undefined;
      });
    };

    const focusVenue = (handle) => {
      if (!map || !handle) return;
      const entry = markers.get(handle);
      if (!entry) return;
      const currentZoom = map.getZoom();
      if (typeof currentZoom !== 'number' || currentZoom < CARD_TARGET_ZOOM) {
        map.setZoom(CARD_TARGET_ZOOM);
      }
      if (reducedMotion()) {
        map.setCenter(entry.marker.position);
      } else {
        map.panTo(entry.marker.position);
      }
    };

    const shownMarkers = () => [...markers.values()].filter((entry) => entry.marker.map === map);

    const fitViewport = (detail) => {
      if (!map) return;
      if (detail.origin) {
        const user = { lat: detail.origin.lat, lng: detail.origin.lng };
        const nearest = shownMarkers()
          .map((entry) => ({
            entry,
            distance: haversineMiles(user.lat, user.lng, entry.venue.latitude, entry.venue.longitude),
          }))
          .sort((a, b) => a.distance - b.distance)
          .slice(0, NEAREST_CLUSTER_SIZE);
        if (nearest.length === 0) {
          map.setCenter(user);
          map.setZoom(ORIGIN_ZOOM);
          return;
        }
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(user);
        nearest.forEach(({ entry }) => bounds.extend(entry.marker.position));
        map.fitBounds(bounds, FIT_PADDING_PX);
        return;
      }
      const shown = shownMarkers();
      if (shown.length === 0) {
        map.setCenter(UK_VIEWPORT.center);
        map.setZoom(UK_VIEWPORT.zoom);
        return;
      }
      if (shown.length === 1) {
        map.setCenter(shown[0].marker.position);
        map.setZoom(SINGLE_MARKER_ZOOM);
        return;
      }
      const bounds = new google.maps.LatLngBounds();
      shown.forEach((entry) => bounds.extend(entry.marker.position));
      map.fitBounds(bounds, FIT_PADDING_PX);
    };

    const syncOrigin = (origin) => {
      if (!origin) {
        if (userMarker) userMarker.map = null;
        return;
      }
      if (!userMarker) {
        const el = document.createElement('div');
        el.className = 'quas-stockists__user-pin';
        userMarker = new markerClasses.marker.AdvancedMarkerElement({
          map: null,
          title: 'Your location',
          content: el,
          gmpClickable: false,
          zIndex: 1,
        });
      }
      userMarker.position = { lat: origin.lat, lng: origin.lng };
      userMarker.map = map;
    };

    const syncVisibility = (detail) => {
      latestDetail = detail;
      const eventsMode = Boolean(detail.eventsMode);
      if (canvasEl) canvasEl.hidden = eventsMode;
      if (!map) return;
      if (eventsMode) {
        markers.forEach((entry) => {
          entry.marker.map = null;
        });
        if (userMarker) userMarker.map = null;
        selectMarker(null);
        return;
      }
      const visible = detail.visibleHandles ? new Set(detail.visibleHandles) : null;
      markers.forEach((entry, handle) => {
        entry.marker.map = visible ? (visible.has(handle) ? map : null) : map;
      });
      const nextSelected =
        detail.selectedHandle && markers.has(detail.selectedHandle) ? detail.selectedHandle : null;
      selectMarker(nextSelected);
      syncOrigin(detail.origin);
      fitViewport(detail);
    };

    const mountMap = () => {
      const { Map } = markerClasses.maps;
      const { AdvancedMarkerElement } = markerClasses.marker;
      canvasEl = document.createElement('div');
      canvasEl.className = 'quas-stockists__map-canvas';
      shell.append(canvasEl);
      try {
        map = new Map(canvasEl, {
          mapId: config.mapId,
          center: UK_VIEWPORT.center,
          zoom: UK_VIEWPORT.zoom,
          disableDefaultUI: true,
          cameraControl: true,
          keyboardShortcuts: true,
          mapTypeControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false,
          scaleControl: false,
          gestureHandling: 'cooperative',
          isFractionalZoomEnabled: true,
          clickableIcons: false,
        });
      } catch (error) {
        canvasEl.remove();
        throw new Error('mount');
      }
      if (typeof map.getMapCapabilities === 'function') {
        const capabilities = map.getMapCapabilities();
        if (capabilities && capabilities.isAdvancedMarkersAvailable === false) {
          canvasEl.remove();
          throw new Error('capability');
        }
      }
      dataset.venues.forEach((venue) => {
        if (!isMappable(venue)) return;
        const marker = new AdvancedMarkerElement({
          map,
          position: { lat: venue.latitude, lng: venue.longitude },
          title: venue.name,
          gmpClickable: true,
          content: new markerClasses.marker.PinElement(pinStyles().base),
        });
        marker.addEventListener('gmp-click', () => {
          selectMarker(venue.handle);
          root.dispatchEvent(
            new CustomEvent('quas-stockists:select', { detail: { handle: venue.handle, source: 'marker' } })
          );
        });
        markers.set(venue.handle, { marker, venue });
      });
    };

    const onReady = () => {
      if (state.phase !== 'loading') return;
      state.phase = 'ready';
      if (state.timer) window.clearTimeout(state.timer);
      state.timer = null;
      // Google can report authentication failure after the map has mounted.
      if (loadingEl) loadingEl.hidden = true;
      if (errorEl) errorEl.hidden = true;
      if (staticEl) staticEl.hidden = true;
      setStatus('ready');
      root.querySelectorAll('[data-show-on-map]').forEach((button) => {
        button.hidden = false;
      });
      const initial = { ...latestDetail };
      const pendingHandle = pendingCardSelection;
      if (pendingHandle) initial.selectedHandle = pendingHandle;
      pendingCardSelection = null;
      syncVisibility(initial);
      if (pendingHandle) focusVenue(pendingHandle);
      root.dispatchEvent(
        new CustomEvent('quas-stockist-map:ready', { detail: { markers: markers.size } })
      );
    };

    const failureReason = (error) => {
      const message = error && error.message;
      return ['script-error', 'import', 'mount', 'capability', 'auth', 'timeout'].includes(message)
        ? message
        : 'mount';
    };

    const beginLoad = () => {
      if (state.phase !== 'idle' || latestDetail.eventsMode) return;
      state.phase = 'loading';
      setStatus('loading');
      if (staticEl) staticEl.hidden = true;
      if (loadingEl) loadingEl.hidden = false;
      installAuthHook();
      authFailureHandlers.add(onAuthFailure);
      state.timer = window.setTimeout(() => fail('timeout'), LOAD_TIMEOUT_MS);
      loadGoogleScript(config.key)
        .then(() => {
          if (state.phase !== 'loading') throw new Error('aborted');
          return Promise.all([
            google.maps.importLibrary('maps'),
            google.maps.importLibrary('marker'),
          ]).then(
            ([mapsLibrary, markerLibrary]) => {
              if (!mapsLibrary || !markerLibrary) throw new Error('import');
              markerClasses = { maps: mapsLibrary, marker: markerLibrary };
            },
            () => {
              throw new Error('import');
            }
          );
        })
        .then(() => {
          if (state.phase !== 'loading') throw new Error('aborted');
          mountMap();
          onReady();
        })
        .catch((error) => {
          if (error && error.message === 'aborted') return;
          fail(failureReason(error));
        });
    };

    const armObserver = () => {
      if (state.phase !== 'idle' || latestDetail.eventsMode) return;
      if (!('IntersectionObserver' in window)) {
        beginLoad();
        return;
      }
      state.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            state.observer.disconnect();
            beginLoad();
          }
        },
        { rootMargin: OBSERVER_ROOT_MARGIN }
      );
      state.observer.observe(shell);
    };

    root.addEventListener('quas-stockists:applied', (event) => {
      const detail = event.detail || {
        visibleHandles: null,
        origin: null,
        selectedHandle: null,
        eventsMode: false,
      };
      if (detail.eventsMode && state.observer) {
        state.observer.disconnect();
        state.observer = null;
      }
      syncVisibility(detail);
      if (!detail.eventsMode && state.phase === 'idle') armObserver();
    });

    root.addEventListener('quas-stockists:select', (event) => {
      const detail = event.detail || {};
      if (detail.source !== 'card' || typeof detail.handle !== 'string') return;
      if (state.phase === 'idle') {
        pendingCardSelection = detail.handle;
        beginLoad();
        return;
      }
      if (state.phase !== 'ready') {
        pendingCardSelection = detail.handle;
        return;
      }
      const entry = markers.get(detail.handle);
      if (!entry) return;
      selectMarker(detail.handle);
      focusVenue(detail.handle);
    });

    root.querySelectorAll('[data-show-on-map]').forEach((button) => {
      button.addEventListener('click', beginLoad);
    });

    // Attach after first paint so list rendering and the LCP candidate schedule first.
    window.requestAnimationFrame(() => window.requestAnimationFrame(armObserver));
  };

  document.querySelectorAll('[data-quas-stockists]').forEach(init);
})();
